import React from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Card } from '@shared-components/atoms/Card';
import { useDecision } from '@optimizely/react-sdk';
import { Grid, Theme } from '@mui/material';
import useWindowSize, { isMobile } from '@/common/useWindowSize';
import boxShadows from '@/common/boxShadows';
import colors from '@/common/colors';
import DealLink from '../DealLink';
import { MerchandiseBanner as MerchandiseBannerContent } from './types';
import { MerchandiseBannerPlacement, useMerchandisingAnalytics } from './hooks/useMerchandiseBannerAnalytics';

interface StyleProps {
  flat?: boolean;
}

const useMerchandiseStyles = makeStyles<Theme, StyleProps>(() => createStyles({
  wrapper: {
  },
  card: {
    overflow: 'hidden',
    lineHeight: 0,
    boxShadow: ({ flat }) => flat ? 'none' : boxShadows.cardShadow,
    backgroundColor: colors.white
  },
  image: {
    width: '100%',
    height: '100%'
  }
}));

interface MerchandiseBannerProps {
  merchandiseBanner: MerchandiseBannerContent;
  flat?: boolean;
  placement: MerchandiseBannerPlacement;
}

export const MerchandiseBannerLegacyV2 = ({ merchandiseBanner, placement, flat }: MerchandiseBannerProps) => {
  const windowSize = useWindowSize();
  const mobileDefault = isMobile(windowSize);
  const [{ enabled: merchandiseBannerEnabled }] = useDecision('fr_dtg_990_bounceback_banner');
  const { trackClick } = useMerchandisingAnalytics(merchandiseBanner, placement, merchandiseBannerEnabled);
  const merchandiseClasses = useMerchandiseStyles({ flat });

  if (!merchandiseBannerEnabled) {
    return null;
  }

  const link = merchandiseBanner.targetUrl ? {
    link: merchandiseBanner.targetUrl,
    altText: merchandiseBanner.altText
  } : null;

  const cardContent = (
      <Card className={merchandiseClasses.card} data-testid="merchandiseBannerCard">
        <img
          src={mobileDefault ? merchandiseBanner.mobileImage.url : merchandiseBanner.desktopImage.url }
          alt={merchandiseBanner.altText}
          className={merchandiseClasses.image}
          loading="lazy"
          data-testid="merchandiseBannerImage"
        />
      </Card>
  )

  if (!link) {
    return (
      <Grid className={merchandiseClasses.wrapper}>{cardContent}</Grid>
    )
  }

  return (
    <DealLink
      tabIndex={0}
      className={merchandiseClasses.wrapper}
      testId="merchandiseBannerLink"
      dataAnalyticsCategory="merchandiseBanner"
      dataAnalyticsAction={link.link}
      onClick={trackClick}
      {...link}
      external={false}
    >
      {cardContent}
    </DealLink>
  );
};
